import * as React from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export interface StatCardProps {
  title: string;
  value: string | number;
  /** Sub-line shown below the value, e.g. "+ 2 this week". */
  subtitle?: string;
  /** Optional lucide-react icon component. Rendered top-right. */
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
  /** Optional stable hook for e2e selectors (e.g. "total-users"). */
  testId?: string;
  /**
   * Visual lane. Both tones share the neutral card surface — colour is
   * carried by the icon chip and the subtitle accent, not the whole
   * card. `success` (default) tints those mint/sea-green for positive
   * metrics; `neutral` keeps them grey. Per the design language, never
   * mix mint with rose in the same component.
   */
  tone?: "success" | "neutral";
  /**
   * Optional left-edge accent (CHP-522) — "colour by exception". Most metric
   * cards stay neutral (omit this); set e.g. `accent="amber"` on a tile that
   * needs a gentle nudge (pending registrations) without a loud full tint.
   */
  accent?: "coral" | "amber" | "red" | "success" | "info" | "neutral";
  /**
   * Zero-state. When `empty`, the card shows `emptyHint` (muted) in place of
   * the value — e.g. "No active deals yet" rather than a bare 0.
   */
  empty?: boolean;
  emptyHint?: string;
  /**
   * When set, the card becomes an interactive button (e.g. a scorecard
   * that filters a list on click). Adds hover/focus affordances and
   * keyboard activation (Enter/Space). Omit for a static stat.
   */
  onClick?: () => void;
  /**
   * Optional control rendered in the header row (left of the icon), e.g. the
   * CHP-842 period picker. Clicks/keys inside it are isolated from the card's
   * own onClick so an embedded control never triggers the drill-down.
   */
  action?: React.ReactNode;
}

export function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  className,
  testId,
  tone = "neutral",
  accent,
  empty = false,
  emptyHint = "No data yet",
  onClick,
  action,
}: StatCardProps) {
  const isSuccess = tone === "success";
  const interactive = !!onClick;
  return (
    <Card
      variant="kpi"
      accent={accent}
      className={cn(
        "flex-1",
        interactive &&
          "cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lifted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      data-testid={testId ? `stat-${testId}` : undefined}
      {...(interactive
        ? {
            role: "button" as const,
            tabIndex: 0,
            onClick,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      <div className="flex items-center justify-between gap-3">
        <p className="min-w-0 text-sm font-medium text-muted-foreground">{title}</p>
        <div className="flex shrink-0 items-center gap-1.5">
          {action ? (
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {action}
            </div>
          ) : null}
          {Icon ? <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground/70" /> : null}
        </div>
      </div>
      {empty ? (
        <p className="text-sm text-muted-foreground">{emptyHint}</p>
      ) : (
        // CHP review-r2 (#70cc8b60): metric text must never run over the card
        // box. `min-w-0` lets the flex value shrink below its intrinsic width,
        // and `break-words` wraps a long sum/subtitle inside the card instead of
        // overflowing it. Applies to every StatCard across the platform.
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p
            className={cn(
              "min-w-0 break-words font-heading text-kpi font-semibold tabular-nums",
              isSuccess ? "text-success" : "text-foreground",
            )}
            data-testid={testId ? `stat-${testId}-value` : undefined}
          >
            {value}
          </p>
          {subtitle ? (
            <p
              className={cn(
                "min-w-0 break-words font-mono text-2xs",
                isSuccess ? "font-medium text-success" : "text-muted-foreground",
              )}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}
